import { useEffect, useState } from 'react';
import Papa from 'papaparse';
import { DataGrid } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';

type CSVViewerProps = {
  fileUrl?: string;
};

type Column = {
  key: string;
  name: string;
  resizable: boolean;
};

export function CSVViewer({ fileUrl }: CSVViewerProps) {
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);
  const [localFileUrl, setLocalFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeFileUrl = fileUrl ?? localFileUrl;

  useEffect(() => {
    if (!activeFileUrl) return;

    const fetchCSV = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('CSVViewer - Fetching CSV from:', activeFileUrl);

        const response = await fetch(activeFileUrl as string);
        console.log('CSVViewer - Response status:', response.status);

        if (!response.ok) {
          throw new Error(`Failed to fetch CSV: ${response.status}`);
        }

        const text = await response.text();
        console.log('CSVViewer - CSV text length:', text.length);
        console.log('CSVViewer - First 200 chars:', text.substring(0, 200));

        const result = Papa.parse<Record<string, string>>(text, { header: true });
        console.log('CSVViewer - Parse result:', result);

        const data = (result.data || []).filter(
          (r) => r && Object.keys(r).length > 1
        );
        console.log('CSVViewer - Filtered data length:', data.length);

        if (data.length > 0) {
          const cols = Object.keys(data[0]).map((key) => ({
            key,
            name: key,
            resizable: true,
          }));
          console.log('CSVViewer - Columns:', cols);
          setColumns(cols);
          setRows(data);
        } else {
          console.warn('CSVViewer - No data after filtering');
        }
      } catch (err) {
        console.error('CSVViewer - Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load CSV');
      } finally {
        setLoading(false);
      }
    };

    fetchCSV();
  }, [activeFileUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setLocalFileUrl(blobUrl);
    }
  };

  if (!activeFileUrl) {
    return (
      <div>
        <input type="file" accept=".csv" onChange={handleFileChange} />
        <p>CSV</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Loading CSV...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  if (columns.length === 0 || rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No data to display</p>
      </div>
    );
  }

  return (
    <DataGrid
      columns={columns}
      rows={rows}
      className="rdg-light h-full min-w-[926px] border border-gray-250v2 rounded-lg no-focus-outline"
      rowHeight={32}
      rowClass={() => 'text-zinc-900'}
      headerRowHeight={38}
      headerRowClass="text-gray-550 bg-zync-50 text-sm font-medium"
    />
  );
}
